import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiCreditCard,
    FiShield,
    FiTruck,
    FiHome,
    FiPlus,
    FiPrinter,
    FiFileText,
    FiMapPin
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import fineService from "../../services/fineService";
import transportService from "../../services/transportService";
import { createHostelFeeReceipt } from "../../services/hostelService";
import ConfirmModal from "../../components/common/ConfirmModal";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const ROOM_TYPE_RENTS = {
    Single: 4000,
    Double: 3000,
    Triple: 1500,
    Dormitory: 1500,
};

const getMonthlyRent = (alloc) => {
    if (!alloc) return 0;
    if (alloc.room?.roomRent !== undefined && alloc.room?.roomRent !== null && Number(alloc.room.roomRent) > 0) {
        return Number(alloc.room.roomRent);
    }
    if (alloc.room?.rentPerMonth) return Number(alloc.room.rentPerMonth);
    if (alloc.room?.roomCharge) return Number(alloc.room.roomCharge);
    const type = alloc.room?.roomType || "Double";
    return ROOM_TYPE_RENTS[type] || 3000;
};

const MODE_COLORS = {
    Cash: "#16a34a", Cheque: "#003b67", NEFT: "#d97706", UPI: "#7c3aed", DD: "#6b7280",
};

export default function FeeStudentDetail() {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const { role } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";
    const finesPath = role === "clerk" ? "/clerk/fines" : role === "accountant" ? "/accountant/fines" : "/fines";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Quick Waive Modal
    const [waiveTarget, setWaiveTarget] = useState(null);
    const [waiverReason, setWaiverReason] = useState("");
    const [submittingWaive, setSubmittingWaive] = useState(false);

    // Quick Pay Modal for Fine/Charge
    const [payTarget, setPayTarget] = useState(null);
    const [payForm, setPayForm] = useState({
        paymentMode: "Cash",
        receiptNo: "",
        transactionRef: "",
        paymentDate: new Date().toLocaleDateString("en-IN"),
    });
    const [submittingPay, setSubmittingPay] = useState(false);

    // Quick Pay Modal for Bus / Transport
    const [showBusPayModal, setShowBusPayModal] = useState(false);
    const [busMonthsPaid, setBusMonthsPaid] = useState(1);
    const [busPayForm, setBusPayForm] = useState({
        paymentMode: "Cash",
        receiptNo: "",
        transactionRef: "",
        paymentDate: new Date().toLocaleDateString("en-IN"),
        remarks: "",
    });
    const [submittingBusPay, setSubmittingBusPay] = useState(false);

    // Quick Pay Modal for Hostel Room Rent
    const [showHostelPayModal, setShowHostelPayModal] = useState(false);
    const [hostelMonthsPaid, setHostelMonthsPaid] = useState(1);
    const [hostelPayForm, setHostelPayForm] = useState({
        paymentMode: "Cash",
        receiptNo: "",
        transactionRef: "",
        paymentDate: new Date().toLocaleDateString("en-IN"),
        remarks: "",
    });
    const [submittingHostelPay, setSubmittingHostelPay] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await feeService.getStudentSummary(studentId);
            setData(res.data || res);
        } catch (err) {
            toast.error(err.message || "Failed to load student fee summary");
            navigate(feeBasePath);
        } finally {
            setLoading(false);
        }
    }, [studentId, navigate, feeBasePath]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await feeService.remove(deleteTarget._id);
            toast.success(`Receipt ${deleteTarget.receiptNo} Deleted`);
            setDeleteTarget(null);
            fetchData();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const handleWaiveFine = async (e) => {
        e.preventDefault();
        if (!waiverReason.trim()) {
            toast.error("Waiver reason is required");
            return;
        }
        try {
            setSubmittingWaive(true);
            await fineService.waiveFine(waiveTarget._id, waiverReason);
            toast.success("Item waived successfully");
            setWaiveTarget(null);
            setWaiverReason("");
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to waive item");
        } finally {
            setSubmittingWaive(false);
        }
    };

    const handlePayFine = async (e) => {
        e.preventDefault();
        try {
            setSubmittingPay(true);
            const prefix = payTarget.category === "Charge" ? "CHG" : "FINE";
            await fineService.payFine(payTarget._id, {
                paymentMode: payForm.paymentMode,
                receiptNo: payForm.receiptNo || `${prefix}-${Date.now().toString().slice(-6)}`,
                paymentDate: payForm.paymentDate || new Date().toLocaleDateString("en-IN"),
                transactionRef: payForm.transactionRef,
            });
            toast.success("Payment collected & marked as Settled");
            setPayTarget(null);
            setPayForm({ paymentMode: "Cash", receiptNo: "", transactionRef: "", paymentDate: new Date().toLocaleDateString("en-IN") });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to record payment");
        } finally {
            setSubmittingPay(false);
        }
    };

    const handleCollectBusFee = async (e) => {
        e.preventDefault();
        if (!data?.transport?._id) return;
        try {
            setSubmittingBusPay(true);
            const numMonths = Number(busMonthsPaid) || 1;
            const res = await transportService.collectFee(data.transport._id, {
                monthsPaid: numMonths,
                amountPaid: numMonths * Number(data.transport.monthlyFee || 0),
                paymentMode: busPayForm.paymentMode,
                paymentDate: busPayForm.paymentDate,
                receiptNo: busPayForm.receiptNo,
                transactionRef: busPayForm.transactionRef,
                remarks: busPayForm.remarks,
            });
            toast.success(res.data?.message || "Bus fee collected successfully");
            setShowBusPayModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to collect bus fee");
        } finally {
            setSubmittingBusPay(false);
        }
    };

    const handleCollectHostelRent = async (e) => {
        e.preventDefault();
        if (!data?.hostelAllocation?._id) return;
        try {
            setSubmittingHostelPay(true);
            const alloc = data.hostelAllocation;
            const numMonths = Number(hostelMonthsPaid) || 1;
            const monthlyRate = getMonthlyRent(alloc);
            const totalRent = numMonths * monthlyRate;

            await createHostelFeeReceipt({
                student: studentId,
                hostel: alloc.hostel?._id || alloc.hostel,
                hostelAllocation: alloc._id,
                academicYear: data.student?.academicYear?._id || data.student?.academicYear,
                installmentNo: (data.receipts?.length || 0) + 1,
                installmentLabel: `${numMonths} Month${numMonths > 1 ? "s" : ""} Hostel Rent (${alloc.room?.roomType || "Room"})`,
                amountPaid: totalRent,
                breakdown: [{ head: `${alloc.room?.roomType || "Hostel"} Room Rent (${numMonths} mo)`, amount: totalRent }],
                paymentDate: hostelPayForm.paymentDate,
                paymentMode: hostelPayForm.paymentMode,
                receiptNo: hostelPayForm.receiptNo,
                transactionRef: hostelPayForm.transactionRef,
                remarks: hostelPayForm.remarks,
            });
            toast.success(`Hostel rent of ${inr(totalRent)} recorded successfully`);
            setShowHostelPayModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to record hostel rent");
        } finally {
            setSubmittingHostelPay(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading student fee profile…</div>;
    if (!data) return null;

    const {
        student: s,
        feesDecided = 0,
        totalTuitionPaid = 0,
        pendingBalance = 0,
        receipts = [],
        chargesSummary = {},
        finesSummary = {},
        allFinesAndCharges = [],
        transport = null,
        hostelAllocation = null,
        overall = {},
    } = data;

    const collectedPct = feesDecided > 0 ? Math.round((totalTuitionPaid / feesDecided) * 100) : 0;
    const totalPendingDues = (overall.totalPending !== undefined) ? overall.totalPending : (pendingBalance + (chargesSummary.pendingAmount || 0) + (finesSummary.pendingAmount || 0));
    const isCleared = totalPendingDues === 0 && feesDecided > 0;

    return (
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
            {/* Header */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>{s.fullName}</h1>
                        {isCleared ? (
                            <span style={{ background: "#dcfce7", color: "#15803d", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700 }}>
                                ✓ All Fees &amp; Fines Cleared
                            </span>
                        ) : (
                            <span style={{ background: "#fef3c7", color: "#92400e", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700 }}>
                                ⏳ Total Outstanding: {inr(totalPendingDues)}
                            </span>
                        )}
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {s.enrollmentNo ? `Reg: ${s.enrollmentNo} · ` : ""}
                        {s.courseAdmitted || s.course?.courseName || ""} Yr {s.courseYear || s.yearNumber || "1"}
                        {s.academicYear?.label && ` · ${s.academicYear.label}`}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button className="btn btn-outline" onClick={() => navigate(-1)}>← Back</button>
                    <Link to={`${feeBasePath}/student/${studentId}/statement`} className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiPrinter size={14} /> Multi-Year Statement
                    </Link>
                    <Link to={`${feeBasePath}/new`} className="btn btn-primary" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiPlus size={14} /> Collect Tuition Fee
                    </Link>
                    <Link to={finesPath} className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: "0.4rem", borderColor: "#dc2626", color: "#dc2626" }}>
                        <FiShield size={14} /> Issue Fine / Charge
                    </Link>
                </div>
            </div>

            {/* KPI Metric Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #003b67" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#003b67" }}>{inr(feesDecided)}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Tuition Decided</div>
                    <div style={{ fontSize: 11, color: "#003b67", marginTop: 2 }}>Paid: {inr(totalTuitionPaid)}</div>
                </div>

                <div className="card" style={{ padding: "1rem", borderLeft: `4px solid ${pendingBalance === 0 ? "#16a34a" : "#dc2626"}` }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: pendingBalance === 0 ? "#16a34a" : "#dc2626" }}>
                        {inr(pendingBalance)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Pending Tuition Balance</div>
                    <div style={{ fontSize: 11, color: pendingBalance === 0 ? "#16a34a" : "#dc2626", marginTop: 2 }}>
                        {pendingBalance === 0 ? "Tuition cleared ✓" : "Installments pending"}
                    </div>
                </div>

                <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #0891b2" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#0891b2" }}>
                        {inr(chargesSummary.pendingAmount || 0)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Pending Charges (Uniform/Manual)</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                        Issued: {inr(chargesSummary.totalIssued || 0)} · Paid: {inr(chargesSummary.totalPaid || 0)}
                    </div>
                </div>

                <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #dc2626" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#dc2626" }}>
                        {inr(finesSummary.pendingAmount || 0)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Pending Fines (Lab/Library)</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                        Issued: {inr(finesSummary.totalIssued || 0)} · Waived: {inr(finesSummary.totalWaived || 0)}
                    </div>
                </div>

                <div className="card" style={{ padding: "1rem", borderLeft: `4px solid ${transport ? "#7c3aed" : "#94a3b8"}` }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: transport ? "#7c3aed" : "var(--text-muted)" }}>
                        {transport ? inr(transport.monthlyFee) + "/mo" : "None"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                        Bus Pass: {transport ? transport.route?.routeName || "Active" : "Not Enrolled"}
                    </div>
                </div>
            </div>

            {/* Tuition Collection Progress Bar */}
            {feesDecided > 0 && (
                <div className="card" style={{ marginBottom: "1.25rem" }}>
                    <div className="card-body" style={{ paddingTop: "0.75rem", paddingBottom: "0.75rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "var(--text-muted)", marginBottom: 6 }}>
                            <span>Tuition Fee Progress</span>
                            <span style={{ fontWeight: 700, color: collectedPct >= 100 ? "#16a34a" : "#d97706" }}>
                                {collectedPct}% Paid ({inr(totalTuitionPaid)} of {inr(feesDecided)})
                            </span>
                        </div>
                        <div style={{ height: 10, borderRadius: 99, background: "var(--border)", overflow: "hidden" }}>
                            <div style={{
                                height: "100%", borderRadius: 99, transition: "width 0.5s",
                                width: `${Math.min(100, collectedPct)}%`,
                                background: collectedPct >= 100 ? "#16a34a" : collectedPct >= 50 ? "#d97706" : "#dc2626",
                            }} />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Active Facilities (Bus & Hostel) ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                {/* Transport Box */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiTruck color="#003b67" /> Bus / Transport Facility
                        </span>
                        {transport ? (
                            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                <span className="badge badge-success" style={{ fontSize: "11px" }}>Active Pass</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setBusMonthsPaid(1);
                                        setBusPayForm({
                                            paymentMode: "Cash",
                                            receiptNo: `BUS-${Date.now().toString().slice(-6)}`,
                                            transactionRef: "",
                                            paymentDate: new Date().toLocaleDateString("en-IN"),
                                            remarks: "",
                                        });
                                        setShowBusPayModal(true);
                                    }}
                                    className="btn btn-primary btn-sm"
                                    style={{ fontSize: "11.5px", padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                                >
                                    <FiCreditCard size={11} /> Collect Bus Fee
                                </button>
                            </div>
                        ) : null}
                    </div>
                    <div className="card-body">
                        {transport ? (
                            <div>
                                <div style={{ fontWeight: 700, fontSize: "14px", color: "var(--text)" }}>
                                    {transport.route?.routeName} {transport.route?.busNumber ? `(${transport.route.busNumber})` : ""}
                                </div>
                                <div style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "4px", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                    <FiMapPin size={12} color="#003b67" /> Pickup Stop: <strong>{transport.stopName}</strong>
                                </div>
                                <div style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px" }}>
                                    Distance: <strong>{transport.distanceKm} km</strong> · Monthly Rate: <strong style={{ color: "#16a34a" }}>₹{transport.monthlyFee}/month</strong>
                                </div>
                                <div style={{ fontSize: "12px", color: "#475569", marginTop: "6px", background: "#f8fafc", padding: "4px 8px", borderRadius: 4, display: "flex", justifyContent: "space-between" }}>
                                    <span>Total Collected: <strong>₹{transport.totalCollected || 0}</strong></span>
                                    <span>Valid Until: <strong>{transport.validUntil ? new Date(transport.validUntil).toLocaleDateString("en-IN") : "Payment Pending"}</strong></span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: "var(--text-muted)", fontSize: "13px" }}>
                                No active bus pass assigned.
                            </div>
                        )}
                    </div>
                </div>

                {/* Hostel Box */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiHome color="#7c3aed" /> Hostel Accommodation
                        </span>
                        {hostelAllocation ? (
                            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                <span className="badge badge-success" style={{ fontSize: "11px" }}>Allocated</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setHostelMonthsPaid(1);
                                        setShowHostelPayModal(true);
                                    }}
                                    className="btn btn-primary btn-sm"
                                    style={{ fontSize: "11.5px", padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: "0.3rem", background: "#7c3aed", borderColor: "#7c3aed", cursor: "pointer" }}
                                >
                                    <FiCreditCard size={11} /> Collect Rent
                                </button>
                            </div>
                        ) : null}
                    </div>
                    <div className="card-body">
                        {hostelAllocation ? (
                            <div>
                                <div style={{ fontWeight: 700, fontSize: "14px", color: "var(--text)" }}>
                                    {hostelAllocation.hostel?.hostelName || hostelAllocation.hostel?.name || "Hostel"} · Room {hostelAllocation.room?.roomNumber || "—"}
                                </div>
                                <div style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "4px" }}>
                                    Bed: <strong>{hostelAllocation.bedNumber || hostelAllocation.bed?.bedNumber || "—"}</strong> · Room Type: <strong>{hostelAllocation.room?.roomType || "Double"}</strong>
                                </div>
                                <div style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px" }}>
                                    Room Rent: <strong style={{ color: "#16a34a" }}>₹{getMonthlyRent(hostelAllocation).toLocaleString("en-IN")}/month</strong>
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: "var(--text-muted)", fontSize: "13px" }}>
                                Not residing in college hostel.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Institutional Charges & Fines Ledger ── */}
            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                        <FiShield color="#dc2626" /> Student Charges &amp; Fines Ledger ({allFinesAndCharges.length})
                    </span>
                    <Link to={finesPath} className="btn btn-outline btn-sm">+ Issue Fine / Charge</Link>
                </div>
                {allFinesAndCharges.length === 0 ? (
                    <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: "13px" }}>
                        ✓ No institutional charges or fines levied against this student.
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Category &amp; Title</th>
                                    <th>Amount</th>
                                    <th>Issued Date</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allFinesAndCharges.map((f) => (
                                    <tr key={f._id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{
                                                    fontSize: "10.5px",
                                                    fontWeight: 700,
                                                    padding: "1px 6px",
                                                    borderRadius: 4,
                                                    background: f.category === "Charge" ? "#e8f1f8" : "#fef2f2",
                                                    color: f.category === "Charge" ? "#002747" : "#b91c1c",
                                                    border: `1px solid ${f.category === "Charge" ? "#b6d5ec" : "#fecaca"}`,
                                                }}>
                                                    {f.category === "Charge" ? "👔 CHARGE" : "⚖ FINE"}
                                                </span>
                                                <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-muted)" }}>{f.fineType}</span>
                                            </div>
                                            <div style={{ fontWeight: 700, color: "var(--text)", marginTop: 2 }}>{f.title}</div>
                                            {f.remarks && (
                                                <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{f.remarks}</div>
                                            )}
                                        </td>
                                        <td style={{ fontWeight: 700, color: f.status === "Paid" ? "#16a34a" : f.status === "Waived" ? "#64748b" : "#dc2626" }}>
                                            {inr(f.amount)}
                                        </td>
                                        <td style={{ fontSize: "12.5px" }}>
                                            {f.issuedDate ? new Date(f.issuedDate).toLocaleDateString("en-IN") : "—"}
                                        </td>
                                        <td style={{ fontSize: "12.5px" }}>
                                            {f.dueDate ? new Date(f.dueDate).toLocaleDateString("en-IN") : "—"}
                                        </td>
                                        <td>
                                            <span className={`badge ${f.status === "Paid" ? "badge-success" : f.status === "Waived" ? "badge-gray" : "badge-danger"}`}>
                                                {f.status === "Paid" ? "✓ Paid" : f.status === "Waived" ? "⚖ Waived" : "⌛ Pending"}
                                            </span>
                                            {f.waiverReason && (
                                                <div style={{ fontSize: "10.5px", color: "#64748b", marginTop: "2px" }}>
                                                    Reason: {f.waiverReason}
                                                </div>
                                            )}
                                            {f.paymentDetails?.receiptNo && (
                                                <div style={{ fontSize: "10.5px", color: "#16a34a", marginTop: "2px" }}>
                                                    {f.paymentDetails.paymentMode} · {f.paymentDetails.receiptNo}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                {f.status === "Pending" && (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => { setPayTarget(f); }}
                                                            style={{ fontSize: "11px", padding: "3px 8px" }}
                                                        >
                                                            <FiCreditCard size={11} /> Pay
                                                        </button>
                                                        <button
                                                            className="btn btn-outline btn-sm"
                                                            onClick={() => { setWaiveTarget(f); }}
                                                            style={{ fontSize: "11px", padding: "3px 8px", color: "#7c3aed", borderColor: "#c4b5fd" }}
                                                        >
                                                            ⚖ Waive
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Fee Receipts Table (Tuition & Academic Installments) ── */}
            <div className="card" style={{ marginBottom: "1.5rem" }}>
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiFileText /> Tuition Fee Installments ({receipts.length})
                    </span>
                    <Link to={`${feeBasePath}/new`} className="btn btn-primary btn-sm">+ Add Tuition Receipt</Link>
                </div>

                {receipts.length === 0 ? (
                    <div className="empty-state">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                        </svg>
                        <p>No tuition receipts recorded yet for this student.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Receipt No.</th>
                                    <th>Installment / Purpose</th>
                                    <th>Amount Paid</th>
                                    <th>Date</th>
                                    <th>Mode</th>
                                    <th>Ref / Cheque</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {receipts.map((r) => {
                                    const mc = MODE_COLORS[r.paymentMode] || "#6b7280";
                                    return (
                                        <tr key={r._id}>
                                            <td style={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 700, color: "#003b67" }}>
                                                {r.receiptNo}
                                            </td>
                                            <td>
                                                <span className="badge badge-blue" style={{ fontSize: 11 }}>#{r.installmentNo}</span>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{r.installmentLabel}</div>
                                                {r.breakdown && r.breakdown.length > 0 && (
                                                    <div style={{ fontSize: "10.5px", color: "#64748b", marginTop: "2px" }}>
                                                        {r.breakdown.map((b) => `${b.head} (₹${b.amount})`).join(", ")}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ fontWeight: 800, color: "#16a34a", fontSize: "14px" }}>{inr(r.amountPaid)}</td>
                                            <td style={{ fontSize: 13 }}>{r.paymentDate}</td>
                                            <td>
                                                <span style={{ background: mc + "22", color: mc, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>
                                                    {r.paymentMode}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                {r.chequeNo || r.transactionRef || "—"}
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                    <button className="btn btn-success btn-sm"
                                                        onClick={() => navigate(`${feeBasePath}/receipts/${r._id}/print`)} title="Print Receipt">🖨</button>
                                                    <button className="btn btn-outline btn-sm"
                                                        onClick={() => navigate(`${feeBasePath}/receipts/${r._id}/edit`)}>Edit</button>
                                                    <button className="btn btn-danger btn-sm"
                                                        onClick={() => setDeleteTarget(r)}>Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: "#f8fafc" }}>
                                    <td colSpan={2} style={{ fontWeight: 700, padding: "0.75rem 1rem", fontSize: 13 }}>Total Tuition Paid</td>
                                    <td style={{ fontWeight: 800, color: "#16a34a", padding: "0.75rem 1rem", fontSize: 15 }}>{inr(totalTuitionPaid)}</td>
                                    <td colSpan={4} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Quick Pay Modal for Fine/Charge ── */}
            {payTarget && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "460px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800 }}>Record Payment &amp; Settle</h3>
                            <button onClick={() => setPayTarget(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handlePayFine} style={{ padding: "1.25rem" }}>
                            <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: 700 }}>{payTarget.title} ({payTarget.fineType})</div>
                                <div style={{ fontSize: "14px", color: "#16a34a", fontWeight: 800, marginTop: "4px" }}>
                                    Amount to Collect: {inr(payTarget.amount)}
                                </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Payment Mode *</label>
                                    <select
                                        className="form-control"
                                        value={payForm.paymentMode}
                                        onChange={(e) => setPayForm({ ...payForm, paymentMode: e.target.value })}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI / QR</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="NEFT">NEFT / NetBanking</option>
                                        <option value="DD">Demand Draft</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>Payment Date *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={payForm.paymentDate}
                                        onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                                <button type="button" className="btn btn-outline" onClick={() => setPayTarget(null)}>Cancel</button>
                                <button type="submit" className="btn btn-success" disabled={submittingPay}>
                                    {submittingPay ? "Recording…" : `Confirm ${inr(payTarget.amount)} Payment`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Quick Waive Modal ── */}
            {waiveTarget && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "440px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800 }}>⚖ Waive Item</h3>
                            <button onClick={() => setWaiveTarget(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handleWaiveFine} style={{ padding: "1.25rem" }}>
                            <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: 700 }}>{waiveTarget.fineType}: {waiveTarget.title}</div>
                                <div style={{ fontSize: "13px", color: "#002747", marginTop: "2px" }}>
                                    Waiving Amount: <strong>₹{waiveTarget.amount}</strong>
                                </div>
                            </div>
                            <div className="form-group" style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Justification / Waiver Reason *
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    placeholder="Enter justification (e.g. Special concession approved)"
                                    value={waiverReason}
                                    onChange={(e) => setWaiverReason(e.target.value)}
                                    required
                                />
                            </div>
                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                                <button type="button" className="btn btn-outline" onClick={() => setWaiveTarget(null)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ background: "#7c3aed", borderColor: "#7c3aed" }} disabled={submittingWaive}>
                                    {submittingWaive ? "Waiving…" : "Confirm Waiver"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Bus / Transport Fee Collection Modal ── */}
            {showBusPayModal && transport && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "460px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <FiTruck color="#003b67" /> Collect Bus Transport Fee
                            </h3>
                            <button onClick={() => setShowBusPayModal(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handleCollectBusFee} style={{ padding: "1.25rem" }}>
                            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: 700, color: "#166534" }}>{transport.route?.routeName} {transport.route?.busNumber ? `(${transport.route.busNumber})` : ""}</div>
                                <div style={{ fontSize: "12.5px", color: "#15803d", marginTop: "2px" }}>
                                    Stop: {transport.stopName} ({transport.distanceKm} km) · Monthly Rate: <strong>₹{transport.monthlyFee}</strong>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Months to Pay *
                                    </label>
                                    <select
                                        className="form-control"
                                        value={busMonthsPaid}
                                        onChange={(e) => setBusMonthsPaid(Number(e.target.value))}
                                    >
                                        <option value={1}>1 Month (₹{transport.monthlyFee * 1})</option>
                                        <option value={2}>2 Months (₹{transport.monthlyFee * 2})</option>
                                        <option value={3}>3 Months (₹{transport.monthlyFee * 3})</option>
                                        <option value={6}>6 Months (₹{transport.monthlyFee * 6})</option>
                                        <option value={10}>10 Months (₹{transport.monthlyFee * 10})</option>
                                        <option value={12}>12 Months (₹{transport.monthlyFee * 12})</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Total Amount Due
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={inr(busMonthsPaid * transport.monthlyFee)}
                                        disabled
                                        style={{ fontWeight: 700, color: "#16a34a", background: "#f8fafc" }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Payment Mode *
                                    </label>
                                    <select
                                        className="form-control"
                                        value={busPayForm.paymentMode}
                                        onChange={(e) => setBusPayForm({ ...busPayForm, paymentMode: e.target.value })}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI / QR</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="NEFT">NEFT / Netbanking</option>
                                        <option value="DD">Demand Draft (DD)</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Receipt / Pass No.
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Auto-generated"
                                        value={busPayForm.receiptNo}
                                        onChange={(e) => setBusPayForm({ ...busPayForm, receiptNo: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Transaction Ref / UTR (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. UPI Ref / Bank Txn ID"
                                    value={busPayForm.transactionRef}
                                    onChange={(e) => setBusPayForm({ ...busPayForm, transactionRef: e.target.value })}
                                />
                            </div>

                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                                <button type="button" className="btn btn-outline" onClick={() => setShowBusPayModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-success" disabled={submittingBusPay}>
                                    {submittingBusPay ? "Processing…" : `Confirm ${inr(busMonthsPaid * transport.monthlyFee)} Bus Fee Payment`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Hostel Rent Collection Modal ── */}
            {showHostelPayModal && hostelAllocation && (
                <div className="modal-overlay" style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1050,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
                }}>
                    <div className="card" style={{ width: "100%", maxWidth: "470px" }}>
                        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <FiHome color="#7c3aed" /> Collect Hostel Room Rent
                            </h3>
                            <button onClick={() => setShowHostelPayModal(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px" }}>✕</button>
                        </div>
                        <form onSubmit={handleCollectHostelRent} style={{ padding: "1.25rem" }}>
                            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem" }}>
                                <div style={{ fontWeight: 700, color: "#5b21b6" }}>
                                    {hostelAllocation.hostel?.hostelName || hostelAllocation.hostel?.name || "Hostel"} · Room {hostelAllocation.room?.roomNumber || "—"} ({hostelAllocation.room?.roomType || "Double"})
                                </div>
                                <div style={{ fontSize: "12.5px", color: "#6d28d9", marginTop: "2px" }}>
                                    Bed Number: <strong>{hostelAllocation.bedNumber || hostelAllocation.bed?.bedNumber || "—"}</strong> · Monthly Rent: <strong>₹{getMonthlyRent(hostelAllocation).toLocaleString("en-IN")}/month</strong>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Months to Pay *
                                    </label>
                                    <select
                                        className="form-control"
                                        value={hostelMonthsPaid}
                                        onChange={(e) => setHostelMonthsPaid(Number(e.target.value))}
                                    >
                                        <option value={1}>1 Month (₹{getMonthlyRent(hostelAllocation) * 1})</option>
                                        <option value={2}>2 Months (₹{getMonthlyRent(hostelAllocation) * 2})</option>
                                        <option value={3}>3 Months (₹{getMonthlyRent(hostelAllocation) * 3})</option>
                                        <option value={6}>6 Months (₹{getMonthlyRent(hostelAllocation) * 6})</option>
                                        <option value={10}>10 Months (₹{getMonthlyRent(hostelAllocation) * 10})</option>
                                        <option value={12}>12 Months (₹{getMonthlyRent(hostelAllocation) * 12})</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Total Rent Due
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={inr(hostelMonthsPaid * getMonthlyRent(hostelAllocation))}
                                        disabled
                                        style={{ fontWeight: 700, color: "#16a34a", background: "#f8fafc" }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Payment Mode *
                                    </label>
                                    <select
                                        className="form-control"
                                        value={hostelPayForm.paymentMode}
                                        onChange={(e) => setHostelPayForm({ ...hostelPayForm, paymentMode: e.target.value })}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI / QR</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="NEFT">NEFT / Netbanking</option>
                                        <option value="DD">Demand Draft (DD)</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                        Payment Date *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={hostelPayForm.paymentDate}
                                        onChange={(e) => setHostelPayForm({ ...hostelPayForm, paymentDate: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "1rem" }}>
                                <label style={{ fontSize: "12px", fontWeight: 700, display: "block", marginBottom: "4px" }}>
                                    Transaction Ref / Receipt No. (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. UPI Ref / Bank Txn ID"
                                    value={hostelPayForm.transactionRef}
                                    onChange={(e) => setHostelPayForm({ ...hostelPayForm, transactionRef: e.target.value })}
                                />
                            </div>

                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                                <button type="button" className="btn btn-outline" onClick={() => setShowHostelPayModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary" style={{ background: "#7c3aed", borderColor: "#7c3aed" }} disabled={submittingHostelPay}>
                                    {submittingHostelPay ? "Processing…" : `Confirm ${inr(hostelMonthsPaid * getMonthlyRent(hostelAllocation))} Hostel Rent Payment`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Confirm Delete Modal ── */}
            {deleteTarget && (
                <ConfirmModal
                    title="Delete Receipt"
                    message={`Delete receipt ${deleteTarget.receiptNo} (${inr(deleteTarget.amountPaid)})? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}